
import React, { useMemo, useState, useRef, useEffect } from "react";
import { graphql, navigate } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import Layout from "~/components/Layout";
import FlexibleContent from "~/components/FlexibleContent";
import Seo from "gatsby-plugin-wpgraphql-seo";
import CustomSEO from "~/components/seo/CustomSEO";
import { BoldTailHeading } from "~/utils/boldTailHeading";

// Page-specific CSS
import "~/assets/css/hero-search.css";

// Date range (popup)
import { DateRange } from "react-date-range";
import { format, addDays, isBefore, isEqual, startOfToday } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useLocation } from "react-use"
import { trustpilotLinkProps } from '~/data/trustpilot'

/** ──────────────────────────────────────────────────────────────
 *  Search card with consistent rows:
 *   • City (popover list; DEFAULT London, cannot be empty)
 *   • Date range (popover; optional)
 *   • Guests (popover stepper; optional)
 *   • SEARCH opens target with params in a new tab
 *
 *  City IDs:
 *   - London → 7917
 *   - Paris  → 1435
 *
 *  Required URL param order:
 *   cities → checkin → checkout → max_guest → reset
 *  ────────────────────────────────────────────────────────────── */
function SearchCard() {
  // CITY — default to London; no "empty" state
  const [location, setLocation] = useState("7917");
  const [openCity, setOpenCity] = useState(false);
  const cityRef = useRef(null);

  const CITIES = [
    { id: "7917", label: "London" },
    { id: "1435", label: "Paris" },
  ];
  const url = useLocation();
  const syncCityToUrl = (cityId) => {
    const params = new URLSearchParams(url.search || "");
    params.set("cities", cityId);
    const base = url.pathname.split("?")[0];
    navigate(`${base}?${params.toString()}`, { replace: true });
  };
  const queryParams = new URLSearchParams(url.search);
  const cityId = queryParams.get("cities");

  // Find the matching city in the array
  const matchedCity = CITIES.find((city) => city.id === cityId);

  // If no match or id missing, show the placeholder prompt (still defaults to London internally)
  const cityLabel = matchedCity ? matchedCity.label : "Where to next?";


  useEffect(() => {
    if (cityId) {
      setLocation(cityId);
    }
  }, [cityId]);
  useEffect(() => {
    function onAway(e) {
      if (!openCity) return;
      if (cityRef.current && !cityRef.current.contains(e.target)) setOpenCity(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpenCity(false);
    }
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openCity]);

  // DATES
  const today = startOfToday();
  const [selection, setSelection] = useState({
    startDate: today,
    endDate: addDays(today, 1),
    key: "selection",
  });
  const [datesSet, setDatesSet] = useState(false);
  const [openCal, setOpenCal] = useState(false);
  const calRef = useRef(null);

  const dateLabel = datesSet
    ? `${format(selection.startDate, "dd MMM yy")} - ${format(
      selection.endDate,
      "dd MMM yy"
    )}`
    : "Dates";

  useEffect(() => {
    function onAway(e) {
      if (!openCal) return;
      if (calRef.current && !calRef.current.contains(e.target)) setOpenCal(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpenCal(false);
    }
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openCal]);

  // GUESTS (null = not set)
  const [guests, setGuests] = useState(null);
  const [openGuests, setOpenGuests] = useState(false);
  const guestsRef = useRef(null);
  const lastFocusPart = useRef(0); // 0=start, 1=end

  const handleRangeFocusChange = ([_, part]) => {
    // when user finishes picking the end date, focus jumps back to start (1 -> 0)
    if (lastFocusPart.current === 1 && part === 0) {
      setOpenCal(false);
    }
    lastFocusPart.current = part;
  };

  const handleRangeChange = (r) => {
    let next = r.selection;
    // normalize: ensure at least 1 night
    if (isBefore(next.endDate, next.startDate) || isEqual(next.endDate, next.startDate)) {
      next = { ...next, endDate: addDays(next.startDate, 1) };
    }
    setSelection(next);
    setDatesSet(true);

    // defensive: if we were on the end part, also close here
    if (lastFocusPart.current === 1) {
      setOpenCal(false);
    }
  };
  const guestsLabel =
    guests == null ? "Guest" : `${guests} ${guests === 1 ? "Guest" : "Guests"}`;

  useEffect(() => {
    function onAway(e) {
      if (!openGuests) return;
      if (guestsRef.current && !guestsRef.current.contains(e.target)) setOpenGuests(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpenGuests(false);
    }
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openGuests]);

  // SEARCH → build the query string in the exact order required
  const handleSearch = () => {
    const parts = [];

    // 1) city (always present)
    parts.push(`cities=${encodeURIComponent(location)}`);

    // 2) dates (optional; also fix endDate if same/before start)
    if (datesSet) {
      let { startDate, endDate } = selection;
      if (isBefore(endDate, startDate) || isEqual(endDate, startDate)) {
        endDate = addDays(startDate, 1);
        setSelection((s) => ({ ...s, endDate }));
      }
      parts.push(`checkin=${encodeURIComponent(format(startDate, "yyyy-MM-dd"))}`);
      parts.push(`checkout=${encodeURIComponent(format(endDate, "yyyy-MM-dd"))}`);
    }

    // 3) guests (optional)
    if (guests != null) {
      parts.push(`max_guest=${encodeURIComponent(String(guests))}`);
    }

    // 4) reset (explicitly last)
    parts.push(`reset=true`);

    // const url = `https://localhost:4200/property-to-rent?${parts.join("&")}`;
    const citySlug = location === "7917" ? "london" : "paris";

    const url = `https://stay.cityrelay.com/property-to-rent/${citySlug}?${parts.join("&")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <form className="lh-card" onSubmit={(e) => e.preventDefault()}>
      {/* CITY */}
      <div className={`lh-field ${openCity ? "is-open" : ""}`} ref={cityRef}>
        <button
          type="button"
          className={`lh-input lh-input--button ${!matchedCity ? "is-placeholder" : ""}`}
          onClick={() => setOpenCity((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={openCity}
        >
          {cityLabel}
        </button>

        <svg className="lh-caret" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" onClick={() => setOpenCity((v) => !v)} style={{ cursor: "pointer" }}>
          <path d="M12.0005 15.7121L5.9905 9.70211L7.4035 8.28711L12.0005 12.8871L16.5965 8.28711L18.0105 9.70111L12.0005 15.7121Z" fill="#12203A" />
        </svg>

        {openCity && (
          <div className="lh-popover">
            <div className="lh-popover-inner">
              <ul className="lh-list" role="listbox" aria-label="Choose a city">
                {CITIES.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`lh-option ${location === c.id ? "is-active" : ""}`}
                      onClick={() => {
                        setLocation(c.id);
                        syncCityToUrl(c.id);
                        setOpenCity(false);
                      }}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className="lh-divider" aria-hidden="true" />

      {/* DATES */}
      <div className={`lh-field lh-field--dates ${openCal ? "is-open" : ""}`} ref={calRef}>
        <button
          type="button"
          className={`lh-input lh-input--button ${!datesSet ? "is-placeholder" : ""}`}
          onClick={() => setOpenCal((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={openCal}
        >
          {dateLabel}
        </button>

        <svg className="lh-caret" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" onClick={() => setOpenCal((v) => !v)} style={{ cursor: "pointer" }}>
          <path d="M12.0005 15.7121L5.9905 9.70211L7.4035 8.28711L12.0005 12.8871L16.5965 8.28711L18.0105 9.70111L12.0005 15.7121Z" fill="#12203A" />
        </svg>

        {openCal && (
          <div className="lh-popover">
            <div className="lh-popover-inner">
              <div className="lh-popover-head">
                <button
                  type="button"
                  className="lh-clear"
                  onClick={() => {
                    setDatesSet(false);
                    setSelection({
                      startDate: today,
                      endDate: addDays(today, 1),
                      key: "selection",
                    });
                  }}
                >
                  CLEAR
                </button>
              </div>

              <DateRange
                ranges={[selection]}
                onChange={handleRangeChange}
                onRangeFocusChange={handleRangeFocusChange}
                months={1}
                direction="horizontal"
                moveRangeOnFirstSelection={false}
                showDateDisplay={false}
                weekdayDisplayFormat="EE"
                minDate={today}
              />

              {/* <div className="lh-popover-actions">
                <button type="button" className="lh-apply" onClick={() => setOpenCal(false)}>
                  APPLY
                </button>
              </div> */}
            </div>
          </div>
        )}
      </div>
      <div className="lh-divider" aria-hidden="true" />

      {/* GUESTS */}
      <div className={`lh-field ${openGuests ? "is-open" : ""}`} ref={guestsRef}>
        <button
          type="button"
          className={`lh-input lh-input--button ${guests == null ? "is-placeholder" : ""}`}
          onClick={() => setOpenGuests((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={openGuests}
        >
          {guestsLabel}
        </button>

        <svg className="lh-caret" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" onClick={() => setOpenGuests((v) => !v)} style={{ cursor: "pointer" }}>
          <path d="M12.0005 15.7121L5.9905 9.70211L7.4035 8.28711L12.0005 12.8871L16.5965 8.28711L18.0105 9.70111L12.0005 15.7121Z" fill="#12203A" />
        </svg>

        {openGuests && (
          <div className="lh-popover">
            <div className="lh-popover-inner" style={{ width: "300px" }}>
              <div className="lh-popover-head">
                <button type="button" className="lh-clear" onClick={() => setGuests(null)}>
                  CLEAR
                </button>
              </div>

              <div className="lh-stepper">
                <button
                  type="button"
                  className="lh-stepper-btn"
                  aria-label="Decrease guests"
                  onClick={() =>
                    setGuests((g) => {
                      if (g == null) return null;
                      return Math.max(1, g - 1);
                    })
                  }
                  disabled={guests == null || guests <= 1}
                >
                  −
                </button>

                <div className="lh-stepper-value">
                  {guests == null ? (
                    <em>Not set</em>
                  ) : (
                    <>
                      <strong>{guests}</strong> {guests === 1 ? "Adult" : "Adults"}
                    </>
                  )}
                </div>

                <button
                  type="button"
                  className="lh-stepper-btn"
                  aria-label="Increase guests"
                  onClick={() => setGuests((g) => (g == null ? 1 : g + 1))}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="lh-divider" aria-hidden="true" />

      <button type="button" className="lh-btn" onClick={handleSearch}>
        Search
        <svg className="lh-btn-arrow" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M15.7121 12.0005L9.70211 5.9905L8.28711 7.4035L12.8871 12.0005L8.28711 16.5965L9.70111 18.0105L15.7121 12.0005Z" fill="#12203A" />
        </svg>
      </button>
    </form>
  );
}


/** Rating line shown under the search card */
function TrustpilotLine() {
  return (
    <div className="fmh-trustpilot">
      <p className="fmh-trustpilot-text">
        Rated <strong>4.7</strong> / 5 based on <strong>2,035 reviews</strong>. Showing our favourite reviews.
      </p>
      <a {...trustpilotLinkProps}>
        <StaticImage
          src="../assets/images/tp-logo.png"
          alt="Trustpilot"
          className="fmh-trustpilot-logo"
          placeholder="none"
        />
      </a>
    </div>
  );
}

/** Full-bleed photo hero: centered heading/search card/rating over a background image */
function LandingHero({ hero, heroTwoCol }) {
  const imageUrl = useMemo(
    () => hero?.image?.sourceUrl || heroTwoCol?.image?.sourceUrl || "/findmyhome-hero.jpg",
    [hero, heroTwoCol]
  );

  const heading = hero?.textCard?.heading?.text ?? "Feel at home, wherever you go";
  const sub =
    hero?.textCard?.content ??
    "Wherever you're headed, CityRelay offers curated homes that match your lifestyle.";

  return (
    <section className="fmh-hero">
      <img src={imageUrl} alt="" loading="eager" fetchpriority="high" className="fmh-hero-bg" />
      <div className="fmh-hero-overlay" aria-hidden="true" />
      <div className="fmh-hero-content">
        <div className="fmh-hero-text">
          <h1 className="fmh-heading">
            <BoldTailHeading text={heading} />
          </h1>
          <p className="fmh-sub">{sub}</p>
        </div>
        <div className="fmh-hero-search-group">
          <SearchCard />
          <TrustpilotLine />
        </div>
      </div>
    </section>
  );
}

export default function FindMyHome({ data: { wpPage } }) {

  const content = wpPage?.page?.page?.content || [];

  const hero = content.find?.((b) => b?.fieldGroupName === "page_Page_Page_Content_Hero")?.hero;
  const heroTwoColumn = content.find?.(
    (b) => b?.fieldGroupName === "page_Page_Page_Content_HeroTwoColumn"
  )?.heroTwoColumn;

  const customSeo = {
    ...wpPage?.seo,
    image: hero?.image ? hero?.image : heroTwoColumn?.image,
  };

  return (
    <Layout
      headerWhite={wpPage.page.page.whiteHeader ?? true}
      backgroundColour={wpPage.page.page.backgroundColour}
      showLogin={wpPage.page.page.showLogin}
      footerCtaType={wpPage.page.page.footerCtaType}
      footerResourcesType={wpPage.page.page.footerResourcesType}
      pageLanguage={wpPage.page.page.pageLanguage}
    >
      <Seo post={wpPage} />
      <CustomSEO seo={customSeo} />
      <LandingHero hero={hero} heroTwoCol={heroTwoColumn} />
      <FlexibleContent content={content} />
    </Layout>
  );
}

/* GraphQL fragment stays unchanged */
export const pageQuery = graphql`
  query landingPageQuery($id: String!) {
    wpPage(id: { eq: $id }) {
      title
      id
      ...SEO
      page {
        page {
          backgroundColour
          whiteHeader
          showLogin
          footerCtaType
          footerResourcesType
          pageLanguage
          content {
            ... on WpPage_Page_Page_Content_TwoColumns {
              fieldGroupName
              twoColumns {
                fieldGroupName
                alignImage
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding { top bottom }
                }
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
                image { ...Image }
                secondImage { ...Image }
                thirdImage { ...Image }
                alignSecondImage
              }
            }
            ... on WpPage_Page_Page_Content_Testimonial {
              fieldGroupName
              testimonial {
                fieldGroupName
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding { top bottom }
                }
                quote
                author { role name fieldGroupName }
                hidetrustpilotwidget
                quoteTagType
                link { ...Button }
                innerBackgroundColour
                alignImage
                image { ...Image }
              }
            }
            ... on WpPage_Page_Page_Content_Features {
              fieldGroupName
              features {
                fieldGroupName
                layout
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
                features {
                  content
                  fieldGroupName
                  heading
                  image { ...Image }
                  link { ...Button }
                }
              }
            }
            ... on WpPage_Page_Page_Content_FullWidthImage {
              fieldGroupName
              image { ...Image }
            }
            ... on WpPage_Page_Page_Content_Reviews {
              __typename
              fieldGroupName
              reviews {
                fieldGroupName
                heading
                reviewsData{
                review
                stars
                name
                date
                image {
                  ...Image
                }
                }
                # type
                # rating
                # count
              }
            }
         
            ... on WpPage_Page_Page_Content_Usps {
              fieldGroupName
              usps {
                fieldGroupName
                design
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding { top bottom }
                }
                showArrows
                design
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }

                
                usps {
                  alignText
                  content
                  heading
                  fieldGroupName
                  imageSize
                  link { ...Button }
                  image { ...Image }
                }
              }
            }

            
            ... on WpPage_Page_Page_Content_LatestResources {
              fieldGroupName
              latestResources {
                fieldGroupName
                resourceType
                customResources {
                  resource {
                    ... on WpPost {
                      id
                      uri
                      title
                      excerpt
                      featuredImage { node { ...Image } }
                    }
                  }
                }
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
              }
            }

            ... on WpPage_Page_Page_Content_Hero {
              fieldGroupName
              hero {
                fieldGroupName
                image { ...Image }
                video { ...Video }
                form
                showHelpFeatures
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
                isHomepage
                language
              }
            }
  
            ... on WpPage_Page_Page_Content_TestimonialsSlider {
              fieldGroupName
              testimonialsSlider {
                fieldGroupName
                cursorBackgroundColour
                margin { top bottom }
                testimonials {
                  testimonial {
                    quote
                    image { ...Image }
                    author { name role }
                    hidetrustpilotwidget
                    quoteTagType
                    link { ...Button }
                    innerBackgroundColour
                    alignImage
                  }
                  fieldGroupName
                }
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_TwoColumnFeatures {
              fieldGroupName
              twoColumnFeatures {
                fieldGroupName
                features {
                  content
                  heading
                  illustration { ...Image }
                }
                textCard {
                  illustration { ...Image }
                  subheading
                  subheadingColor
                  alignText
                  heading { text type }
                  content
                  buttons {
                    button {
                      button { button { ...Button } }
                      type
                    }
                  }
                }
              }
            }


            ... on WpPage_Page_Page_Content_Newsletter {
              fieldGroupName
              newsletter {
                fieldGroupName
                illustrationSize
                heading
                content
                illustration { ...Image }
              }
            }

          }
        }
      }
    }
  }
`;


